import React, { lazy, useContext, useEffect, useRef, useState } from "react";
import cx from "clsx";
import {
    IProduct,
    IProductGallery,
    IProductPage
} from "@montana/models/generated/cms.interface";
import { VariationPickers } from "./variation-pickers/variation-pickers.component";
import { ProductInformation } from "./product-page-aside/product-information";
import { Translate } from "@montana/shared/translate";
import { useProduct } from "@montana/containers/product";
import { usePage } from "@montana/containers/page";
import {
    MobileAndTabletPortrait,
    SmallerThanTabletLandscape,
    TabletLandscapeAndLarger
} from "@montana/shared/responsive";
import { SpecificationsSection } from "./specifications";
import { Series } from "./series";
import { GREY } from "@montana/constants/colors";
import { DynamicBlocks } from "@montana/blocks/dynamic-blocks.component";
import { useTracking } from "@montana/containers/tracking";
import {
    AssetProductViewer,
    ZoomedAssetProductViewer
} from "./asset-product-viewer/asset-product-viewer.component";
import { Modal } from "@montana/shared/modal";
import { isBrowser } from "@montana/helpers/browser.helper";
import { FirstRenderContext } from "@montana/context/first-render.context";
import { Media } from "@montana/shared/media";
import { Timing } from "@montana/constants/animation";
import { epiMediaRatioToEnum } from "@montana/helpers/epi-mapping.helper";
import { useTranslations } from "@montana/containers/translations";
import { Breadcrumbs } from "@montana/shared/breadcrumbs";
import { PDPProvider } from "./product-page.context";
import Carousel from "@montana/shared/carousel";
import { use100vh } from "react-div-100vh";
import { staticSettings } from "@montana/helpers/static-settings.helper";
import { InfoToolbox } from "@montana/shared/info-toolbox";
import { Splash } from "@montana/shared/splash/splash.component";
import { ARButton } from "@montana/shared/ar-button/ar-button.component";
import { ARDesktop } from "@montana/shared/ar-desktop/ar-desktop.component";
import { useSettings } from "@montana/containers/settings/settings.container";
import { CUSTOMER_MODE } from "@montana/constants/customer-mode";
import { ImageCarousel } from "./variation-pickers/image-carousel";
import {
    DynamicContentModal,
    IDynamicContentModalRef
} from "@montana/shared/modal/dynamic-content-modal.component";
import { LondonDynamicsImageRendering } from "@montana/constants/london-dynamics";

const ProductViewerOld = lazy(() =>
    import("@montana/shared/product-viewer-old/product-viewer-old.component")
);
const ProductViewer = lazy(() =>
    import("@montana/shared/product-viewer/product-viewer.component")
);

export const ProductPage: React.FC<IProduct & IProductPage> = ({
    topBlocks,
    bottomBlocks,
    configuratorInfoToolBox
}: IProductPage): JSX.Element => {
    const isFirstRender = useContext(FirstRenderContext);
    const [showGallery, setShowGallery] = useState<boolean>(false);
    const [showZoomableViewer, setShowZoomableViewer] = useState<boolean>(
        false
    );
    const [contactMontanaModal, setContactMontanaModal] = useState<boolean>(
        false
    );
    const {
        productTitleH1,
        useAssetImage,
        gallery,
        prices,
        arUrl,
        arExistForIOS,
        arExistForAndroid,
        config,
        imageRenderingMethod
    } = useProduct();
    const { setPageHasHero } = usePage();
    const { trackProductView } = useTracking();
    const translate = useTranslations();
    const modalRefCarousel = useRef<Modal>(null);
    const modalRefContact = useRef<IDynamicContentModalRef>(null);
    const fullHeight = use100vh();

    const { customerMode } = useSettings();

    useEffect(() => {
        setPageHasHero(true);
        trackProductView();
    }, []);

    useEffect(() => {
        if (showGallery && isBrowser) {
            modalRefCarousel.current?.open();
        }
    }, [showGallery]);

    useEffect(() => {
        if (showZoomableViewer && isBrowser) {
            modalRefCarousel.current?.open();
        }
    }, [showZoomableViewer]);

    useEffect(() => {
        if (contactMontanaModal && isBrowser) {
            modalRefContact.current?.open();
        }
    }, [contactMontanaModal]);

    // TODO LD this useeffect might be a better solution if BE provides the image url
    // Currently it is set in the London dynamics image rendering component
    // useEffect(() => {
    //     if(imageRenderingMethod === LondonDynamicsImageRendering && londonDynamicsStaticImageUrl) {
    //         setProductCylindoImage(londonDynamicsStaticImageUrl)
    //     }
    // }, [londonDynamicsStaticImageUrl, imageRenderingMethod, config]);

    return (
        <PDPProvider>
            <section className="product-page container">
                <header className="product-page__hero app__block">
                    <div className="product-page__bg"></div>
                    <div className="product-page__breadcrumb">
                        <Breadcrumbs />
                    </div>
                    <div className="product-page__main">
                        <div className="product-page__top">
                            <MobileAndTabletPortrait>
                                {!!prices?.splash ? (
                                    <Splash {...prices.splash} />
                                ) : (
                                    <div></div>
                                )}
                            </MobileAndTabletPortrait>
                            {!!arUrl &&
                                (arExistForIOS || arExistForAndroid) && (
                                    <ARButton url={arUrl} />
                                )}
                        </div>
                        {customerMode === CUSTOMER_MODE.PROFESSIONAL && (
                            <>
                                <SmallerThanTabletLandscape>
                                    <div className="product-page__viewer">
                                        <ImageCarousel mobileLayout />
                                    </div>
                                </SmallerThanTabletLandscape>
                                <TabletLandscapeAndLarger>
                                    <section
                                        className={`product-page__viewer-professional`}
                                    >
                                        <ImageCarousel />
                                    </section>
                                </TabletLandscapeAndLarger>
                            </>
                        )}
                        {(customerMode === CUSTOMER_MODE.PRIVATE ||
                            customerMode === CUSTOMER_MODE.B2B) && (
                            <>
                                <InfoToolbox
                                    id="prodConfig"
                                    appearTimeMs={2000}
                                    headline={configuratorInfoToolBox?.title}
                                    content={
                                        configuratorInfoToolBox?.bodyMessage
                                    }
                                    label={
                                        configuratorInfoToolBox?.doNotShowMessageAgainText
                                    }
                                />
                                <TabletLandscapeAndLarger>
                                    <VariationPickers
                                        onZoom={() =>
                                            setShowZoomableViewer(true)
                                        }
                                        onShowGallery={() =>
                                            setShowGallery(true)
                                        }
                                    />
                                </TabletLandscapeAndLarger>
                                <div className="product-page__viewer">
                                    {(arExistForIOS || arExistForAndroid) &&
                                        isBrowser &&
                                        imageRenderingMethod !==
                                            LondonDynamicsImageRendering && (
                                            <ARDesktop
                                                cylindoSku={config?.cylindoSku}
                                                dimensions={config?.dimensions}
                                            />
                                        )}
                                    {useAssetImage && (
                                        <>
                                            <AssetProductViewer />
                                            <div className="product-viewer__desktop-zoom">
                                                <button
                                                    onClick={() =>
                                                        setShowZoomableViewer(
                                                            true
                                                        )
                                                    }
                                                    className="zoom__button"
                                                    type="button"
                                                >
                                                    <span>
                                                        {translate(
                                                            (t: any) =>
                                                                t.productPage
                                                                    .dimensionMobileZoom,
                                                            "[Tap to zoom]"
                                                        )}
                                                    </span>
                                                </button>
                                            </div>
                                        </>
                                    )}
                                    {!useAssetImage &&
                                        !isFirstRender &&
                                        isBrowser &&
                                        !showZoomableViewer && (
                                            <React.Suspense fallback={<></>}>
                                                {imageRenderingMethod ===
                                                LondonDynamicsImageRendering ? (
                                                    <ProductViewer
                                                        backgroundColor={GREY}
                                                        onZoom={() =>
                                                            setShowZoomableViewer(
                                                                true
                                                            )
                                                        }
                                                        isZoomOverlayVisible={
                                                            false
                                                        }
                                                    />
                                                ) : (
                                                    <ProductViewerOld
                                                        backgroundColor={GREY}
                                                        onZoom={() =>
                                                            setShowZoomableViewer(
                                                                true
                                                            )
                                                        }
                                                        isZoomOverlayVisible={
                                                            false
                                                        }
                                                    />
                                                )}
                                            </React.Suspense>
                                        )}
                                </div>
                                <SmallerThanTabletLandscape includeSSR>
                                    <VariationPickers
                                        onZoom={() =>
                                            setShowZoomableViewer(true)
                                        }
                                        onShowGallery={() =>
                                            setShowGallery(true)
                                        }
                                    />
                                </SmallerThanTabletLandscape>
                            </>
                        )}
                        <div
                            className={`product-page__information${
                                customerMode === CUSTOMER_MODE.PROFESSIONAL
                                    ? "-professional"
                                    : ""
                            }`}
                        >
                            <ProductInformation
                                setContactMontanaModal={setContactMontanaModal}
                            />
                        </div>
                        <div className="product-page__view-more">
                            <Translate
                                value="productPage.productScrollToViewMore"
                                defaultValue="[Scroll to view more]"
                            />
                        </div>
                    </div>
                </header>
                <DynamicBlocks blocks={topBlocks} />
                <SpecificationsSection />
                {staticSettings.b2c && <Series />}
                <DynamicBlocks blocks={bottomBlocks} />
            </section>
            <Modal
                ref={modalRefCarousel}
                className={cx("product-page__modal", {
                    "product-page__modal--gallery": showGallery,
                    "product-page__modal--zoom": showZoomableViewer
                })}
                onModalClosed={(): void => {
                    setShowGallery(false);
                    setShowZoomableViewer(false);
                }}
                timeout={{
                    appear: Timing.SLOW,
                    enter: 0,
                    exit: Timing.SLOW
                }}
                includeExitButton={true}
                includeBackdrop={false}
                disableBackgroundInteraction
                position="fullscreen"
            >
                {showGallery && isBrowser && !!gallery?.length && (
                    <div
                        className="product-page-gallery"
                        style={{
                            height: fullHeight ? `${fullHeight}px` : "100vh"
                        }}
                    >
                        <Carousel
                            lowHeaderSpacing
                            insideBrokenContainer
                            header={
                                <div className="product-page-gallery__header">
                                    <h4 className="product-page-gallery__title">{`${productTitleH1} ${translate(
                                        t => t.productPage.productPageGallery,
                                        "[Gallery]"
                                    )}`}</h4>
                                </div>
                            }
                        >
                            {gallery?.map(
                                (gc: IProductGallery, index: number) => {
                                    const widthRatio =
                                        1 /
                                        (epiMediaRatioToEnum(
                                            gc.mediaDisplayRatio
                                        ) || 1);
                                    return (
                                        <div
                                            key={`product-page-gallery__card__${index}`}
                                            className={`product-page-gallery__card product-page-gallery__card--${gc.mediaDisplayRatio}`}
                                            style={{
                                                width: !!fullHeight
                                                    ? `${fullHeight *
                                                          0.75 *
                                                          widthRatio}px`
                                                    : `${75 * widthRatio}vh`
                                            }}
                                        >
                                            <Media
                                                className="product-page-gallery__image"
                                                image={gc.image}
                                                mediaRatio={epiMediaRatioToEnum(
                                                    gc.mediaDisplayRatio
                                                )}
                                                sizes={[
                                                    "(min-width: 1367px) 1520px",
                                                    "(min-width: 1000px) 1260px",
                                                    "828px"
                                                ].join(", ")}
                                                placeholder
                                            />
                                            {!!gc.gallaryDescription && (
                                                <p className="product-page-gallery__description">
                                                    {gc.gallaryDescription}
                                                </p>
                                            )}
                                        </div>
                                    );
                                }
                            )}
                        </Carousel>
                    </div>
                )}
                {showZoomableViewer && (
                    <div className="product-page__zoomable-viewer-wrapper">
                        {useAssetImage && <ZoomedAssetProductViewer />}
                        {!useAssetImage ? (
                            imageRenderingMethod ===
                            LondonDynamicsImageRendering ? (
                                <ProductViewer zoomable isZoomOverlayVisible />
                            ) : (
                                <ProductViewerOld
                                    zoomable
                                    isZoomOverlayVisible
                                />
                            )
                        ) : null}
                    </div>
                )}
            </Modal>
            <DynamicContentModal
                className="product-page__modal--contact"
                contentUrl="/da/contact-montana-modal/"
                ref={modalRefContact}
                onModalClosed={() => setContactMontanaModal(false)}
            />
        </PDPProvider>
    );
};
